// SfcAnalysisBar — small floating control surface for the Surface
// Analysis time scrubber. Shows when the 'sfcanalysis' layer is on.
// Range 0 hours (current analysis) through +168h (Day 7) at WPC's
// published step intervals: 6/12/18/24/30/36/48/60/72/96/120/144/168.
// Each step pulls a different transparent PNG from /api/wx/sfcanalysis.

import { useUiStore } from '../stores/uiStore';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useInstrumentPrefsStore } from '../stores/instrumentPrefsStore';
import { useInstrumentStripHeight } from './useInstrumentStripHeight';

const STEPS = [0, 6, 12, 18, 24, 30, 36, 48, 60, 72, 96, 120, 144, 168];
// Slider value is the INDEX into STEPS (so we can use a contiguous
// range slider for the irregular step intervals).
const SLIDER_MIN = 0;
const SLIDER_MAX = STEPS.length - 1;

export default function SfcAnalysisBar() {
  const showSfcAnalysis = useLayerVisibilityStore((s) => s.layers.sfcanalysis);
  const showWxHazards = useLayerVisibilityStore((s) => s.layers.wxhazards);
  const showNexrad = useLayerVisibilityStore((s) => s.layers.nexrad);
  const showInstruments = useInstrumentPrefsStore((s) => s.showInstruments);
  const sfcOffsetH = useUiStore((s) => s.sfcAnalysisOffsetH);
  const setSfcOffsetH = useUiStore((s) => s.setSfcAnalysisOffsetH);

  // Live height of the bottom instrument strip so this bar can stack
  // ABOVE the NEXRAD + GFA bars + instrument cluster cleanly.
  const instrumentH = useInstrumentStripHeight(showSfcAnalysis);

  if (!showSfcAnalysis) return null;

  // Vertical position. Stack above any other bottom bars that are
  // also visible.
  const INSTRUMENT_BOTTOM_OFFSET = 12;
  const ABOVE_INSTRUMENTS_GAP_PX = 14;
  const HAZARD_BAR_FOOTPRINT_PX = 110;
  const NEXRAD_BAR_FOOTPRINT_PX = 70;
  const baseBottomPx = showInstruments
    ? INSTRUMENT_BOTTOM_OFFSET + instrumentH + ABOVE_INSTRUMENTS_GAP_PX
    : 14;
  const bottomPx = baseBottomPx
    + (showWxHazards ? HAZARD_BAR_FOOTPRINT_PX : 0)
    + (showNexrad ? NEXRAD_BAR_FOOTPRINT_PX : 0);

  // Translate the current offset to a slider index.
  const currentIdx = Math.max(0, STEPS.indexOf(sfcOffsetH));
  const onChange = (idx: number) => {
    const clamped = Math.max(0, Math.min(STEPS.length - 1, idx));
    setSfcOffsetH(STEPS[clamped]);
  };

  return (
    <div
      className="absolute left-0 right-0 z-10 flex justify-center pointer-events-none"
      style={{ bottom: `${bottomPx}px` }}
    >
      <div className="pointer-events-auto bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-bar px-3 py-2 w-[min(28rem,calc(100vw-1.5rem))]">
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-start min-w-[5rem]">
            <span className="text-[9px] tracking-widest text-cockpit-400 uppercase">Sfc</span>
            <span className="font-mono text-[12px] text-ember-300 leading-none mt-0.5">
              {labelFor(sfcOffsetH)}
            </span>
          </div>
          <div className="flex-1">
            <input
              type="range"
              min={SLIDER_MIN}
              max={SLIDER_MAX}
              step={1}
              value={currentIdx}
              onChange={(e) => onChange(parseInt(e.currentTarget.value, 10))}
              aria-label="Surface analysis forecast offset"
              className="w-full accent-ember-500"
            />
            <div className="relative h-3 mt-0.5 text-[9px] font-mono text-cockpit-400">
              <span className="absolute" style={{ left: '0%' }}>now</span>
              <span className="absolute -translate-x-1/2" style={{ left: '50%' }}>+36h</span>
              <span className="absolute -translate-x-full" style={{ left: '100%' }}>+7d</span>
            </div>
          </div>
          <div className="text-right min-w-[4rem]">
            <span className="text-[9px] tracking-widest text-cockpit-400 uppercase">Valid</span>
            <div className="font-mono text-[11px] text-cockpit-100 leading-none mt-0.5">
              {wallClockAt(sfcOffsetH)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function labelFor(h: number): string {
  if (h === 0) return 'now';
  if (h < 72) return `+${h}h`;
  return `+Day ${Math.round(h / 24)}`;
}

function wallClockAt(h: number): string {
  const d = new Date(Date.now() + h * 3600_000);
  if (h < 24) {
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    return `${hh}:${mm}Z`;
  }
  // Show date-time for multi-day forecasts.
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  return `${m}/${day} ${hh}Z`;
}
